import { type BaseSyntheticEvent, useState } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, useWatch } from 'react-hook-form';
import { toast } from 'sonner';

import type { ClientId, SessionChargeId } from '@nesso/ledger-contracts/ids';

import { type PaymentMethod, useRecordPaymentOptions } from '@/entities/payment';
import {
    queryKeys as sessionChargeQueryKeys,
    type UnpaidSessionCharge,
} from '@/entities/session-charge';
import { ApiError } from '@/shared/api/http-client';
import { formatCurrencyFromCents } from '@/shared/lib/format-utils';
import { formatLocalDate, isToday, parseLocalDate } from '@/shared/lib/time-utils';

import {
    type RecordPaymentFormOutput,
    type RecordPaymentFormValues,
    recordPaymentFormSchema,
} from './record-payment-form.schema';

type Options = {
    clientId: ClientId;
    unpaid: UnpaidSessionCharge[];
    selectedSessionChargeId: SessionChargeId | null;
    onSuccess: () => void;
};

function toAllocation(charge: UnpaidSessionCharge) {
    return { sessionChargeId: charge.id, amountCents: charge.balanceCents };
}

function sumCents(allocations: { amountCents: number }[]) {
    return allocations.reduce((sum, allocation) => sum + allocation.amountCents, 0);
}

export function useRecordPaymentForm({
    clientId,
    unpaid,
    selectedSessionChargeId,
    onSuccess,
}: Options) {
    const queryClient = useQueryClient();
    const recordPayment = useMutation(useRecordPaymentOptions());
    const [paymentId] = useState(() => crypto.randomUUID());
    const [lockedRequest, setLockedRequest] = useState<NonNullable<
        typeof recordPayment.variables
    > | null>(null);

    const listedCharges = new Map<string, UnpaidSessionCharge>(
        unpaid.map((charge) => [charge.id, charge]),
    );
    const resolveAllocations = (selected: RecordPaymentFormValues['allocations']) =>
        selected.flatMap(({ sessionChargeId }) => {
            const charge = listedCharges.get(sessionChargeId);
            return charge ? [toAllocation(charge)] : [];
        });

    const schemaResolver = zodResolver(recordPaymentFormSchema);

    const form = useForm<RecordPaymentFormValues, unknown, RecordPaymentFormOutput>({
        resolver: (values, context, options) =>
            schemaResolver(
                { ...values, allocations: resolveAllocations(values.allocations) },
                context,
                options,
            ),
        defaultValues: {
            clientId,
            allocations: unpaid
                .filter(
                    (charge) =>
                        selectedSessionChargeId === null || charge.id === selectedSessionChargeId,
                )
                .map(toAllocation),
        },
    });

    const { isSubmitted } = form.formState;
    const liveAllocations = resolveAllocations(
        useWatch({ control: form.control, name: 'allocations' }),
    );
    const allocations = lockedRequest ? lockedRequest.allocations : liveAllocations;
    const paidOn = useWatch({ control: form.control, name: 'paidOn' });
    const method = useWatch({ control: form.control, name: 'method' });
    const selectedIds = new Set<string>(
        allocations.map((allocation) => allocation.sessionChargeId),
    );
    const totalCents = sumCents(allocations);

    const setAllocations = (next: RecordPaymentFormValues['allocations']) => {
        form.setValue('allocations', next, { shouldValidate: isSubmitted });
    };

    const toggleSession = (charge: UnpaidSessionCharge, checked: boolean) => {
        const others = form
            .getValues('allocations')
            .filter((allocation) => allocation.sessionChargeId !== charge.id);
        setAllocations(checked ? [...others, toAllocation(charge)] : others);
    };

    const toggleAllSessions = (checked: boolean) => {
        setAllocations(checked ? unpaid.map(toAllocation) : []);
    };

    const selectMethod = (next: Exclude<PaymentMethod, 'CARD_ON_FILE'>) => {
        form.setValue('method', next, { shouldValidate: isSubmitted });
        if (next !== 'CHECK') {
            form.setValue('checkNumber', undefined, { shouldValidate: isSubmitted });
        }
    };

    const selectPaidDate = (date: Date) => {
        form.setValue('paidOn', isToday(date) ? undefined : formatLocalDate(date), {
            shouldValidate: isSubmitted,
        });
    };

    const send = (request: NonNullable<typeof recordPayment.variables>) => {
        recordPayment.mutate(request, {
            onSuccess: () => {
                void queryClient.invalidateQueries({ queryKey: sessionChargeQueryKeys.all });
                toast.success(
                    `${formatCurrencyFromCents(sumCents(request.allocations))} payment added`,
                );
                onSuccess();
            },
            onError: (error) => {
                if (error instanceof ApiError && error.status === 409) {
                    setLockedRequest(null);
                    form.setError('root', { message: 'The sessions have changed' });
                    void queryClient.invalidateQueries({
                        queryKey: sessionChargeQueryKeys.unpaidList(clientId),
                    });
                    return;
                }
                setLockedRequest(request);
                form.setError('root', { message: "Couldn't record the payment. Try again." });
            },
        });
    };

    const submitNew = form.handleSubmit(({ paidOn: pickedDay, ...data }) => {
        send({ id: paymentId, ...data, ...(pickedDay ? { paidOn: pickedDay } : {}) });
    });

    const onSubmit = (event?: BaseSyntheticEvent) => {
        if (!lockedRequest) {
            return submitNew(event);
        }
        event?.preventDefault();
        form.clearErrors('root');
        send(lockedRequest);
        return Promise.resolve();
    };

    return {
        form,
        onSubmit,
        isPending: recordPayment.isPending,
        isLocked: lockedRequest !== null,
        selectedIds,
        totalCents,
        method,
        paidDate: paidOn ? parseLocalDate(paidOn) : new Date(),
        toggleSession,
        toggleAllSessions,
        selectMethod,
        selectPaidDate,
    };
}
